//codex-- Keep the toolbar style page-scoped and preserve the local API contracts.
import PageToolbar, { ToolbarField } from './PageToolbar';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import HeadingCp from '../components/HeadingCp';
import LoadingSpinner from '../components/LoadingSpinner';
import DateFormat from '../components/DateFormat';
import { toast } from 'react-toastify';
import HoldReportSearchBarCP from '../components/HoldReportSearchBarCP';

const HoldDashboard = () => {
  const apiUrl = process.env.REACT_APP_API_URL;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [typeFilter, setTypeFilter] = useState('all'); 
// values: 'all' | 'maintenance' | 'service' | 'customer'


  // shared filters
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [onlyActive, setOnlyActive] = useState(true);
  const [search, setSearch] = useState('');

  const handleSearch = async () => {
    setLoading(true);
    try {
      await fetchData();
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setSearch('');
    await fetchData();
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build params used by maintenance/service
      const commonParams = { search };
      if (onlyActive) commonParams.active = 1;
      if (from) commonParams.from = from;
      if (to) commonParams.to = to;

      // Customer endpoint ignores from/to (as before)
      const customerParams = { search };
      if (onlyActive) customerParams.active = 1;

      const endpoints = {
        maintenance: `${apiUrl}/staff_report_crud.php?getHoldReport=1`,
        service: `${apiUrl}/staff_report_crud.php?getServiceHoldReport=1`,
        customer: `${apiUrl}/staff_report_crud.php?getCustomerHoldReport=1`,
      };

      const [maintRes, servRes, custRes] = await Promise.allSettled([
        axios.get(endpoints.maintenance, { params: commonParams }),
        axios.get(endpoints.service, { params: commonParams }),
        axios.get(endpoints.customer, { params: customerParams }),
      ]);

      const safeData = (res) =>
        res.status === 'fulfilled'
          ? (Array.isArray(res.value.data) && res.value.data[0] === 'success' ? res.value.data[1] : [])
          : [];

      const maint = safeData(maintRes).map(r => ({
        type: 'maintenance',
        hold_id: r.hold_id,
        ref_id: r.maintenance_id,
        customer_name: r.customer_name || '—',
        job_site: r.job_site || '—',
        trouble: null,
        hold_amount: Number(r.hold_amount || 0),
        on_hold: r.on_hold || null,
        last_off_hold: r.last_off_hold || null,
        is_active: !!r.is_active,
      }));

      const serv = safeData(servRes).map(r => ({
        type: 'service',
        hold_id: r.hold_id,
        ref_id: r.service_call_id,
        customer_name: r.customer_name || '—',
        job_site: r.job_site || '—',
        trouble: r.trouble || '—',
        hold_amount: Number(r.hold_amount || 0),
        on_hold: r.on_hold || null,
        last_off_hold: r.last_off_hold || null,
        is_active: !!r.is_active,
      }));

      const cust = safeData(custRes).map(r => ({
        type: 'customer',
        // customers list may not have a hold_id, use customer_id as stable key but display "—" for hold id
        hold_id: r.hold_id ?? null,
        ref_id: r.customer_id,
        customer_name: r.customer_name || '—',
        job_site: '—',
        trouble: '—',
        hold_amount: Number(r.hold_amount || 0),
        on_hold: null,
        last_off_hold: null,
        is_active: Number(r.hold) ? true : false,
      }));

      // Merge and sort (example: active first, then newest on_hold desc)
      const merged = [...maint, ...serv, ...cust].sort((a, b) => {
        if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
        const aDate = a.on_hold ? new Date(a.on_hold).getTime() : 0;
        const bDate = b.on_hold ? new Date(b.on_hold).getTime() : 0;
        return bDate - aDate;
      });

      setRows(merged);
    } catch (err) {
      console.error(err);
      toast.error('Error fetching holds', { position: 'top-center' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // initial

  const TypeBadge = ({ type }) => {
    const cls =
      type === 'maintenance' ? 'bg-blue-100 text-blue-800' :
      type === 'service' ? 'bg-purple-100 text-purple-800' :
      'bg-amber-100 text-amber-800';
    const label =
      type === 'maintenance' ? 'Maintenance' :
      type === 'service' ? 'Service' : 'Customer';
    return <span className={`mt-1 inline-block px-2 py-0.5 rounded text-[11px] font-semibold ${cls}`}>{label}</span>;
  };

  const filteredRows = typeFilter === 'all'
  ? rows
  : rows.filter(r => r.type === typeFilter);

  return (
    <div className="mx-auto max-w-[1250px] px-4 sm:px-6 lg:px-8 py-8">
      <HeadingCp label="Holds Dashboard" />

      {/* Type Filter */}
<>{/* //codex-- Adapt this page's matching reference toolbar without replacing its handlers. */}<PageToolbar title="Hold report tools" actions={<>
          {['all', 'maintenance', 'service', 'customer'].map(t => <button key={t} onClick={() => setTypeFilter(t)} className={`px-3 py-1 rounded ${typeFilter === t ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-800'}`} aria-pressed={typeFilter === t}>
            {t === 'all' ? 'All' : t === 'maintenance' ? 'Maintenance' : t === 'service' ? 'Service' : 'Customers'}
          </button>)}
        </>}>
          <div>
            <ToolbarField label="From"><input type="date" value={from} onChange={e => setFrom(e.target.value)} className="border rounded px-3 py-2" /></ToolbarField>
          </div>
          <div>
            <ToolbarField label="To"><input type="date" value={to} onChange={e => setTo(e.target.value)} className="border rounded px-3 py-2" /></ToolbarField>
          </div>
          <HoldReportSearchBarCP value={search} onChange={e => setSearch(e.target.value)} onSearch={handleSearch} onReset={handleReset} loading={loading} />
        </PageToolbar></>

      {loading ? (
        <LoadingSpinner>Loading…</LoadingSpinner>
      ) : (
        <table className="min-w-full divide-y divide-gray-300 bg-gray-200 border rounded-lg">
          <thead>
            <tr className="divide-x divide-gray-400">
              <th className="px-3 py-3.5 text-left text-sm font-semibold">Hold ID</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold">Ref ID</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold">Customer</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold">Job Site / Address</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold">Trouble</th>
              <th className="px-3 py-3.5 text-right text-sm font-semibold">Hold Amount</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold">On Hold</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold">Off Hold</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold">Active?</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-6 text-gray-500">No results.</td></tr>
            ) : filteredRows.map((r, i) => (
              <tr key={`${r.type}-${r.hold_id ?? r.ref_id}-${i}`} className={`${i%2===0?'bg-white':'bg-gray-100'} divide-x`}>
                <td className="px-3 py-2 text-sm">
                  <div className="flex flex-col">
                    <span>{r.hold_id ?? '—'}</span>
                    <TypeBadge type={r.type} />
                  </div>
                </td>
                <td className="px-3 py-2 text-sm">{r.ref_id ?? '—'}</td>
                <td className="px-3 py-2 text-sm">{r.customer_name}</td>
                <td className="px-3 py-2 text-sm">{r.job_site}</td>
                <td className="px-3 py-2 text-sm">{r.trouble ?? '—'}</td>
                <td className="px-3 py-2 text-sm text-right">${Number(r.hold_amount).toFixed(2)}</td>
                <td className="px-3 py-2 text-sm">{r.on_hold ? <DateFormat date={r.on_hold}/> : '—'}</td>
                <td className="px-3 py-2 text-sm">{r.last_off_hold ? <DateFormat date={r.last_off_hold}/> : '—'}</td>
                <td className="px-3 py-2 text-sm">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${r.is_active ? 'bg-red-200 text-red-800':'bg-green-200 text-green-800'}`}>
                    {r.is_active ? 'Active' : 'Cleared'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default HoldDashboard;
